import { useCallback, useMemo, useState } from 'react';
import { X } from 'lucide-react';

import type { TrackCard, TrackCardQuery } from '../../api/index.js';
import { useWorkspace } from '../../app/runtime.js';
import { Button } from '../../design-system/button.js';
import { Input } from '../../design-system/form.js';
import { PageHeader, SegmentedControl } from '../shared/page.js';
import { TrackCollection } from '../tracks/track-collection.js';
import { DurationRangeFilter } from './duration-range-filter.js';
import { ONSET_AUDIT_TRACKS } from './onset-audit-data.js';
import {
  ONSET_AUDIT_DURATION_CEILING_SECONDS,
  ONSET_AUDIT_PROJECT_ID,
  durationIsInRange,
  filterOnsetAuditTracks,
  violatesOnsetAuditRule,
  type DurationRange,
  type OnsetAuditFilter,
} from './onset-audit-model.js';

export function OnsetAuditPage() {
  const { connection, library } = useWorkspace();
  const [filter, setFilter] = useState<OnsetAuditFilter>('all');
  const [query, setQuery] = useState('');
  const [selectionMode, setSelectionMode] = useState(false);
  const [durationRange, setDurationRange] = useState<DurationRange>({
    minimum: 0,
    maximum: ONSET_AUDIT_DURATION_CEILING_SECONDS,
  });

  const violatesCount = ONSET_AUDIT_TRACKS.filter(violatesOnsetAuditRule).length;
  const passesCount = ONSET_AUDIT_TRACKS.length - violatesCount;
  const visibleAuditTracks = useMemo(
    () => filterOnsetAuditTracks(ONSET_AUDIT_TRACKS, { filter, query }),
    [filter, query],
  );
  const visibleRankById = useMemo(
    () => new Map(visibleAuditTracks.map((track, index) => [track.id, index])),
    [visibleAuditTracks],
  );
  const durationFilterActive =
    durationRange.minimum !== 0 || durationRange.maximum !== ONSET_AUDIT_DURATION_CEILING_SECONDS;
  const transformTracks = useCallback(
    (tracks: readonly TrackCard[]) =>
      tracks
        .filter(
          (track) =>
            visibleRankById.has(track.id) &&
            (!durationFilterActive || durationIsInRange(track.duration_seconds, durationRange)),
        )
        .sort((left, right) => visibleRankById.get(left.id)! - visibleRankById.get(right.id)!),
    [durationFilterActive, durationRange, visibleRankById],
  );
  const trackQuery = useMemo<TrackCardQuery>(
    () => ({
      connection_id: connection.id,
      library_id: library.id,
      composer_project_id: ONSET_AUDIT_PROJECT_ID,
      favorite: 'any',
      limit: 100,
    }),
    [connection.id, library.id],
  );
  const playbackContext = useMemo(
    () => ({
      kind: 'project' as const,
      id: `kk11-filtered-review:${filter}:${query}:${durationRange.minimum}:${durationRange.maximum}`,
      label: 'KK11 filtered review',
    }),
    [durationRange.maximum, durationRange.minimum, filter, query],
  );
  const selectionScopeKey = `${filter}:${query}:${durationRange.minimum}:${durationRange.maximum}`;

  const detectionOptions = [
    { label: `All ${ONSET_AUDIT_TRACKS.length}`, value: 'all' },
    { label: `Violates ${violatesCount}`, value: 'violates' },
    { label: `Does not violate ${passesCount}`, value: 'does-not-violate' },
  ] as const;

  return (
    <div>
      <PageHeader
        eyebrow="KK11 · filtered review"
        title="Opening-boom filter"
        description="Use the script result only as a loose review filter. Song rows and actions are the same as the standard project and catalog views."
      />

      <section className="mb-5 rounded-md border border-border bg-bg-2 p-4">
        <div className="grid gap-5">
          <SegmentedControl
            label="Script result"
            onChange={setFilter}
            options={detectionOptions}
            value={filter}
          />

          <div className="grid items-end gap-3 sm:grid-cols-[minmax(0,1fr)_auto]">
            <label className="block">
              <span className="mb-2 block font-mono text-xs font-medium uppercase tracking-[0.1em] text-ink-3">
                Search title
              </span>
              <Input
                maxLength={256}
                onChange={(event) => setQuery(event.currentTarget.value)}
                placeholder="Find a KK11 track"
                type="search"
                value={query}
              />
            </label>
            <Button
              className="w-full sm:w-auto"
              onClick={() => setSelectionMode((current) => !current)}
              variant={selectionMode ? 'ghost' : 'secondary'}
            >
              {selectionMode ? <X aria-hidden="true" /> : null}
              {selectionMode ? 'Cancel' : 'Select'}
            </Button>
          </div>

          <DurationRangeFilter onChange={setDurationRange} value={durationRange} />
        </div>
      </section>

      <section aria-label="Filtered KK11 songs">
        <TrackCollection
          emptyDescription="Adjust the filters or title search."
          emptyTitle="No KK11 songs match this view"
          errorTitle="KK11 songs could not be loaded"
          loadAll
          onSelectionModeChange={setSelectionMode}
          playbackContext={playbackContext}
          query={trackQuery}
          selectionMode={selectionMode}
          selectionScopeKey={selectionScopeKey}
          transformTracks={transformTracks}
        />
      </section>
    </div>
  );
}
